import Stomp from "stompjs";
import SockJS from "sockjs-client";

import { neftApi } from "app/config";
import { openNotificationWithIcon } from "utils";

import { removeEmptyValue } from "utils";
import { BatchesSearchParam, ServerDTO, BatchesFinalStatusDTO } from "./models";
import {
  ApiResponse,
  BatchSummaryDTO,
  ApiTableResponse,
  BatchSummaryParams,
  TransactionUpdateDTO,
} from "models";
import { notification } from "antd";

const generationApiWithTag = neftApi.enhanceEndpoints({
  addTagTypes: [
    "audit",
    "generation",
    "batch-summary",
    "transaction-batch",
    "transaction-error",
    "generation-server",
    "batch-status-enum",
    "generation-extract",
  ],
});

const generationApiSlice = generationApiWithTag.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    initiateExtraction: builder.mutation<ApiResponse<null>, { serverId: string }>({
      query: ({ serverId }) => ({
        url: `/transaction/extract/${serverId}`,
        method: "POST",
      }),
      invalidatesTags: (result, error, tags) => [{ type: "audit" }, { type: "batch-summary" }],
    }),

    getServers: builder.query<ApiResponse<ServerDTO[]>, void>({
      query: () => ({
        url: "/server",
        method: "GET",
      }),

      providesTags: [{ type: "generation-server" as const, id: "all" }],
    }),

    getServerLocationById: builder.query<ApiResponse<string>, { id: string }>({
      query: ({ id }) => ({
        url: `/server/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, tags) => [
        { type: "generation-server" as const, id: `${tags.id}` },
      ],
    }),

    getBatchesToInvalidate: builder.query<ApiResponse<string[]>, BatchesSearchParam>({
      query: (params) => ({
        url: `/summary/invalidate`,
        method: "GET",
        params: params,
      }),
      providesTags: (result, error, tags) => [
        { type: "generation-server" as const, id: `${tags.searchParam}` },
      ],
    }),

    getBatchesToUpdateFinalStatus: builder.query<ApiResponse<string[]>, BatchesSearchParam>({
      query: (params) => ({
        url: `/batchId/final`,
        method: "GET",
        params: params,
      }),
      providesTags: (result, error, tags) => [
        { type: "generation-server" as const, id: `${tags.searchParam}` },
      ],
    }),

    searchSummaryBatch: builder.query<ApiTableResponse<BatchSummaryDTO>, BatchSummaryParams>({
      query: (batchSummaryParams) => {
        return {
          url: "/summary/search",
          method: "GET",
          params: removeEmptyValue(batchSummaryParams),
        };
      },
      providesTags: ["batch-summary"],
    }),

    updateBatchTransaction: builder.mutation<ApiResponse<string>, TransactionUpdateDTO>({
      query: (transactionProps) => {
        return {
          url: "/transaction/single",
          method: "PUT",
          body: removeEmptyValue(transactionProps),
        };
      },
      invalidatesTags: (result, error, tags) => [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" as const, id: tags.batchID },
        { type: "transaction-error" as const, id: tags.batchID },
      ],
    }),

    revalidateBatchFinalStatus: builder.mutation<ApiResponse<string>, string>({
      query: (batchId) => {
        return {
          url: `/summary/invalidate/rollback`,
          params: { batchId },
          method: "PUT",
        };
      },
      invalidatesTags: (result, error, tags) => [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" as const, id: tags },
        { type: "transaction-error" as const, id: tags },
      ],
    }),

    invalidateBatchFinalStatus: builder.mutation<ApiResponse<string>, string>({
      query: (batchId) => {
        return {
          url: `/summary/invalidate`,
          body: { batchIds: [batchId] },
          method: "PUT",
        };
      },
      invalidatesTags: (result, error, tags) => [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" as const, id: tags },
        { type: "transaction-error" as const, id: tags },
      ],
    }),

    invalidateBatchesFinalStatus: builder.mutation<ApiResponse<string>, string[]>({
      query: (batchesArr) => {
        return {
          url: `/summary/invalidate/`,
          method: "PUT",
          body: { batchIds: batchesArr },
        };
      },
      invalidatesTags: (result, error, tags) => [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" as const },
        { type: "transaction-error" as const },
      ],
    }),

    requeryBatchesFinalStatus: builder.mutation<ApiResponse<string>, BatchesFinalStatusDTO>({
      query: (batchesFinalStatusObj) => {
        return {
          url: `/pull/final`,
          method: "PUT",
          body: batchesFinalStatusObj,
        };
      },
      invalidatesTags: (result, error, tags) => [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" as const },
        { type: "transaction-error" as const },
      ],
    }),

    requeryBatchFinalStatus: builder.mutation<ApiResponse<string>, string>({
      query: (batchId) => {
        return {
          url: `/pull/final/${batchId}`,
          method: "PUT",
        };
      },
      invalidatesTags: (result, error, tags) => [
        { type: "audit" },
        { type: "batch-summary" },
        { type: "transaction-batch" as const, id: tags },
        { type: "transaction-error" as const, id: tags },
      ],
    }),

    getNotification: builder.query<any, void>({
      invalidatesTags: ["batch-summary"] as any,
      queryFn: () => ({ data: "" }),

      async onCacheEntryAdded(arg, { cacheDataLoaded, cacheEntryRemoved, updateCachedData }) {
        try {
          await cacheDataLoaded;

          const socket = new SockJS("https://nibss-neft-api.k9.isw.la/neft-api-websocket");
          const stompClient = Stomp.over(socket);

          stompClient.connect(
            {},
            (frame) => {
              stompClient.subscribe("/neft/batch", (message: any) => {
                openNotificationWithIcon("Extraction Completed", message.body, "success");
              });
            },

            async (error) => {
              // Log WebSocket connection errors - critical for production monitoring
              console.error("WebSocket connection error:", error);

              // Optionally notify user of connection failure
              notification["error"]({
                maxCount: 2,
                duration: 10,
                key: "web-socket-error",
                placement: "bottomRight",
                message: "WebSocket Connection Error",
                description: "Failed to establish real-time connection. Please refresh the page.",
              });

              await cacheEntryRemoved;
            },
          );

          await cacheEntryRemoved;
          stompClient.abort("connect");
          stompClient.abort("/neft/batch");
        } catch (error) {
          // Log WebSocket errors in all environments - critical for production debugging
          // These errors indicate connectivity issues that affect real-time data synchronization
          console.error("WebSocket error:", error);

          // Optionally notify user of connection failure
          notification["error"]({
            maxCount: 2,
            duration: 10,
            key: "web-socket-error-connection",
            placement: "bottomRight",
            message: "WebSocket Error",
            description: "Real-time updates unavailable. Please refresh the page if issues persist",
          });
        }
      },
    }),
  }),
});

export const {
  useGetServersQuery,
  useGetNotificationQuery,
  useSearchSummaryBatchQuery,
  useGetServerLocationByIdQuery,
  useInitiateExtractionMutation,
  useUpdateBatchTransactionMutation,
  useRequeryBatchFinalStatusMutation,
  useLazyGetBatchesToInvalidateQuery,
  useRequeryBatchesFinalStatusMutation,
  useInvalidateBatchFinalStatusMutation,
  useRevalidateBatchFinalStatusMutation,
  useInvalidateBatchesFinalStatusMutation,
  useLazyGetBatchesToUpdateFinalStatusQuery,
} = generationApiSlice;

export default generationApiSlice;
